/* I bersagli della clonazione: i conti e i wallet su cui il bot può nascere.
 *
 * Il punto di questo file è UNA sola regola di ammissibilità, usata dal
 * disegno delle caselle, dal conteggio, dal riepilogo e dalla creazione.
 * Quando erano tre regole diverse, la casella si spegneva ma il conteggio
 * continuava a includerla, e si finiva per creare bot su conti dichiarati non
 * finanziabili — con denaro vero.
 *
 * Il secondo punto è che un conto inutilizzabile non sparisce: resta a schermo,
 * spento, col motivo accanto. Toglierlo dall'elenco farebbe cercare altrove un
 * conto che si sa di avere.
 */

import { accounts, wallets } from '../../api';
import type { Account, RiepilogoSaldoWallet, SaldiMulticatena, SaldoAccount, Tradebot, Wallet } from '../../api';
import { num } from '../../lib/formato';
import { abbrevia } from './modello';

/** Sotto questa cifra il vincolo di saldo diventa vacuo e si creerebbero bot
 *  da zero, che partono e non possono comprare nulla. */
export const CAPITALE_MINIMO = 1;

export interface Bersaglio {
  tipo: 'account' | 'wallet';
  id: number;
  nome: string;
  /** "CEX · binance" oppure "DEX · catena 56". */
  genere: string;
  /** Quanto capitale libero c'è nella valuta del bot. `null` = non letto. */
  saldoLibero: number | null;
  /** La valuta in cui è espresso il saldo, es. "USDT". */
  valuta: string;
  /**
   * Motivo che non dipende dal capitale scelto: chiave scaduta, sola lettura,
   * gas insufficiente, saldo non leggibile. `null` = nessun impedimento fisso.
   */
  bloccoFisso: string | null;
  /** `true` quando il blocco è grave (soldi o chiavi), non solo una cautela. */
  bloccoGrave: boolean;
}

/* ==========================================================================
 * Costruzione dei bersagli
 * ======================================================================== */

/**
 * Bersagli di tipo account.
 *
 * `GET /accounts/get-all-balances` è lenta e seriale, e il suo campo `error` è
 * già un messaggio in italiano: è lì dentro che arrivano "chiave scaduta",
 * "permessi di sola lettura" e i rifiuti dell'exchange. Si mostra quel testo
 * senza riscriverlo, perché è più preciso di qualsiasi traduzione nostra.
 */
export function bersagliDaConti(
  conti: Account[],
  saldi: SaldoAccount[] | null,
  saldiFalliti: boolean,
  bot: Tradebot,
): Bersaglio[] {
  const perId = new Map<number, SaldoAccount>();
  for (const s of saldi ?? []) if (s.account?.id != null) perId.set(s.account.id, s);

  return conti
    .filter((c) => c.id !== bot.accountId)
    .map((c) => {
      const saldo = perId.get(c.id);
      const genere = `CEX · ${c.exchange?.slug ?? c.exchange?.name ?? 'exchange sconosciuto'}`;

      /* Il bot sorgente gira su exchange: su un wallet mancherebbe tutta la
         configurazione di catena, e viceversa. Si dice, non si nasconde. */
      if (bot.walletId != null) {
        return {
          tipo: 'account' as const,
          id: c.id,
          nome: c.name,
          genere,
          saldoLibero: null,
          valuta: bot.currency,
          bloccoFisso:
            'il bot di origine opera su catena: su un conto exchange non userebbe RPC, gas e slippage che ha configurati',
          bloccoGrave: false,
        };
      }

      if (c.exchange && !c.exchange.isCCXTSupported) {
        return {
          tipo: 'account' as const,
          id: c.id,
          nome: c.name,
          genere,
          saldoLibero: null,
          valuta: bot.currency,
          bloccoFisso: `exchange non più supportato da ccxt: ${c.exchange.slug}`,
          bloccoGrave: true,
        };
      }

      if (saldiFalliti || !saldo) {
        return {
          tipo: 'account' as const,
          id: c.id,
          nome: c.name,
          genere,
          saldoLibero: null,
          valuta: bot.currency,
          bloccoFisso:
            'saldo non letto: non si impegna capitale su un conto che non si è riusciti a interrogare',
          bloccoGrave: true,
        };
      }

      if (saldo.error) {
        return {
          tipo: 'account' as const,
          id: c.id,
          nome: c.name,
          genere,
          saldoLibero: null,
          valuta: bot.currency,
          bloccoFisso: saldo.error,
          bloccoGrave: true,
        };
      }

      /* Il capitale del bot è espresso nella valuta di quotazione, non in
         dollari: confrontarlo col totale in USD del conto darebbe per buono un
         conto pieno di altcoin e vuoto di USDT. */
      const moneta = saldo.coins.find(
        (m) => m.coin.toUpperCase() === bot.currency.toUpperCase(),
      );

      return {
        tipo: 'account' as const,
        id: c.id,
        nome: c.name,
        genere,
        saldoLibero: moneta?.balance ?? 0,
        valuta: bot.currency,
        bloccoFisso: null,
        bloccoGrave: false,
      };
    });
}

/**
 * Bersagli di tipo wallet.
 *
 * Per un bot DEX servono due letture diverse: il saldo nella valuta di
 * quotazione (che decide se il capitale sta in piedi) e il gas nativo della
 * catena (che decide se le transazioni possono essere firmate). Un wallet pieno
 * di USDT e senza BNB non è un wallet utilizzabile: il bot partirebbe e
 * fallirebbe alla prima operazione.
 */
export function bersagliDaWallet(
  portafogli: Wallet[],
  riepiloghi: RiepilogoSaldoWallet[] | null,
  dettagli: Map<number, SaldiMulticatena | null>,
  bot: Tradebot,
): Bersaglio[] {
  const perId = new Map<number, RiepilogoSaldoWallet>();
  for (const r of riepiloghi ?? []) perId.set(r.walletId, r);

  return portafogli
    .filter((w) => w.id !== bot.walletId)
    .map((w) => {
      const nome = `${w.name}${w.address ? ` · ${abbrevia(w.address)}` : ''}`;
      const genere = `DEX · catena ${bot.chainId ?? '?'}`;

      if (bot.walletId == null) {
        return {
          tipo: 'wallet' as const,
          id: w.id,
          nome,
          genere,
          saldoLibero: null,
          valuta: bot.currency,
          bloccoFisso:
            'il bot di origine opera su exchange: gli mancano RPC, catena, gas e slippage per girare su un wallet',
          bloccoGrave: false,
        };
      }

      if (!w.address) {
        return {
          tipo: 'wallet' as const,
          id: w.id,
          nome,
          genere,
          saldoLibero: null,
          valuta: bot.currency,
          bloccoFisso: 'wallet senza indirizzo: non può ricevere né firmare',
          bloccoGrave: true,
        };
      }

      const riepilogo = perId.get(w.id);
      const dettaglio = dettagli.get(w.id);

      if (riepilogo && !riepilogo.ok && !dettaglio) {
        return {
          tipo: 'wallet' as const,
          id: w.id,
          nome,
          genere,
          saldoLibero: null,
          valuta: bot.currency,
          bloccoFisso: riepilogo.error ?? 'lettura on-chain non riuscita: nodo RPC muto',
          bloccoGrave: true,
        };
      }

      if (!dettaglio) {
        return {
          tipo: 'wallet' as const,
          id: w.id,
          nome,
          genere,
          saldoLibero: null,
          valuta: bot.currency,
          bloccoFisso: 'saldi on-chain non letti: non si impegna capitale su un wallet non verificato',
          bloccoGrave: true,
        };
      }

      const catena = dettaglio.chains.find((c) => c.chainId === bot.chainId);

      if (!catena) {
        return {
          tipo: 'wallet' as const,
          id: w.id,
          nome,
          genere,
          saldoLibero: null,
          valuta: bot.currency,
          bloccoFisso: `la catena ${bot.chainId} non è fra quelle scansionate per questo wallet`,
          bloccoGrave: true,
        };
      }

      if (!catena.ok) {
        return {
          tipo: 'wallet' as const,
          id: w.id,
          nome,
          genere,
          saldoLibero: null,
          valuta: bot.currency,
          bloccoFisso: catena.error ?? `nodo RPC di ${catena.name} muto: saldo non verificabile`,
          bloccoGrave: true,
        };
      }

      const gettone = catena.tokens.find(
        (t) => t.symbol.toUpperCase() === bot.currency.toUpperCase(),
      );
      const saldoLibero = gettone ? Number(gettone.balance) : 0;

      /* Il gas viene prima del capitale: senza nativo il bot non firma nulla,
         per quanto USDT abbia in cassa. Il messaggio del server è già scritto
         in forma leggibile e porta il minimo consigliato. */
      if (catena.gasAlert) {
        const nativo = catena.native ? `${num(Number(catena.native.balance), 5)} ${catena.nativeSymbol}` : '—';
        return {
          tipo: 'wallet' as const,
          id: w.id,
          nome,
          genere: `DEX · ${catena.name}`,
          saldoLibero,
          valuta: bot.currency,
          bloccoFisso: `gas insufficiente: ${nativo} in cassa, ne servono almeno ${num(catena.gasAlert.recommendedMinimum, 5)} ${catena.nativeSymbol} — ${catena.gasAlert.message}`,
          bloccoGrave: catena.gasAlert.level !== 'low',
        };
      }

      return {
        tipo: 'wallet' as const,
        id: w.id,
        nome,
        genere: `DEX · ${catena.name}`,
        saldoLibero,
        valuta: bot.currency,
        bloccoFisso: null,
        bloccoGrave: false,
      };
    });
}

/* ==========================================================================
 * L'UNICA regola di ammissibilità
 * ======================================================================== */

/**
 * Perché questo bersaglio non può ricevere il clone, o `null` se può.
 * La usano il disegno delle caselle, il conteggio, il riepilogo e la creazione:
 * una sola funzione, così non possono divergere.
 */
export function motivoBlocco(b: Bersaglio, capitale: number): string | null {
  if (b.bloccoFisso) return b.bloccoFisso;
  if (!Number.isFinite(capitale) || capitale < CAPITALE_MINIMO) return null;
  if (b.saldoLibero == null) return 'saldo non letto';
  if (b.saldoLibero < capitale) {
    const manca = capitale - b.saldoLibero;
    return `saldo libero insufficiente: ci sono ${num(b.saldoLibero, 2)} ${b.valuta}, ne mancano ${num(manca, 2)}`;
  }
  return null;
}

export function ammissibili(bersagli: Bersaglio[], scelti: number[], capitale: number): Bersaglio[] {
  if (!Number.isFinite(capitale) || capitale < CAPITALE_MINIMO) return [];
  return bersagli.filter((b) => scelti.includes(b.id) && motivoBlocco(b, capitale) == null);
}

/* ==========================================================================
 * Validazione del capitale
 * ======================================================================== */

export interface StatoCapitale {
  valido: boolean;
  valore: number;
  motivo: string;
}

export function leggiCapitale(grezzo: string): StatoCapitale {
  const n = Number.parseFloat(grezzo.replace(',', '.'));
  if (grezzo.trim() === '' || !Number.isFinite(n)) {
    return { valido: false, valore: 0, motivo: 'Inserisci il capitale da assegnare a ogni bot.' };
  }
  if (n < CAPITALE_MINIMO) {
    return {
      valido: false,
      valore: n,
      motivo: `Minimo ${num(CAPITALE_MINIMO)} per bot: sotto questa cifra il bot parte e non può comprare nulla.`,
    };
  }
  return { valido: true, valore: n, motivo: '' };
}

/* ==========================================================================
 * Il caricamento dei bersagli, uno solo per i due flussi
 * ======================================================================== */

/** `GET /wallets/balances/summary` risponde 400 oltre 25 id. */
export const MAX_WALLET = 25;

/**
 * Le letture grezze da cui si costruiscono i bersagli.
 *
 * Il caricamento e la costruzione sono separati apposta: le chiamate dipendono
 * SOLO da dove si cerca (conti o wallet), mentre la valuta di quotazione e la
 * catena entrano solo nella derivazione. Tenendoli insieme, un modulo in cui si
 * digita la valuta rilanciava `get-all-balances` — lenta e seriale — a ogni
 * lettera battuta.
 */
export interface DatiBersagli {
  tipo: 'account' | 'wallet';
  conti: Account[];
  saldiConti: SaldoAccount[] | null;
  portafogli: Wallet[];
  riepiloghi: RiepilogoSaldoWallet[] | null;
  dettagli: Map<number, SaldiMulticatena | null>;
  /** `true` quando i saldi non si sono potuti leggere del tutto. */
  saldiMuti: boolean;
  /** Wallet rimasti fuori per il limite di 25 id per richiesta. */
  troncato: number;
}

/**
 * Legge i conti o i wallet su cui un bot può nascere.
 *
 * `riferimento` serve a due cose sole, ed entrambe sono stabili per l'intera
 * vita di un flusso: `walletId` sceglie il ramo (catena o exchange) ed esclude
 * la sede su cui il bot di origine gira già. Nella creazione l'id è −1, che
 * nessun conto ha: non si esclude nulla.
 */
export async function caricaDatiBersagli(
  riferimento: Pick<Tradebot, 'accountId' | 'walletId'>,
  segnale?: AbortSignal,
): Promise<DatiBersagli> {
  const vuoto = {
    conti: [] as Account[],
    saldiConti: null,
    portafogli: [] as Wallet[],
    riepiloghi: null,
    dettagli: new Map<number, SaldiMulticatena | null>(),
  };

  if (riferimento.walletId != null) {
    const elenco = await wallets.elenco({ sort: ['name,ASC'] });
    /* La sede su cui il bot gira già si esclude PRIMA del taglio a 25:
       tagliando prima si perderebbe un bersaglio buono per fare posto a uno che
       verrebbe scartato comunque, e il conteggio dei wallet non elencati
       direbbe uno in più del vero. */
    const possibili = elenco.data.filter((w) => w.id !== riferimento.walletId);
    const candidati = possibili.slice(0, MAX_WALLET);

    const riepiloghi = await wallets
      .riepilogoSaldi({ ids: candidati.map((w) => w.id) }, segnale)
      .catch(() => null);

    /* Il riepilogo dice SE una catena ha un allarme gas, non quanto manca. Per
       il motivo scritto accanto al conto serve la lettura piena, che è in cache
       lato server ~45 s. Una che fallisce non fa cadere le altre. */
    const dettagli = new Map<number, SaldiMulticatena | null>();
    const letture = await Promise.allSettled(
      candidati.map((w) => wallets.saldiTutteLeCatene(w.id, {}, segnale)),
    );
    letture.forEach((l, i) => {
      dettagli.set(candidati[i].id, l.status === 'fulfilled' ? l.value : null);
    });

    return {
      ...vuoto,
      tipo: 'wallet',
      portafogli: candidati,
      riepiloghi,
      dettagli,
      saldiMuti: letture.length > 0 && letture.every((l) => l.status === 'rejected'),
      troncato: possibili.length - candidati.length,
    };
  }

  const [elenco, saldi] = await Promise.all([
    accounts.elenco({ sort: ['name,ASC'] }),
    /* Lenta e seriale: N conti × fetchBalance, nessun timeout complessivo. Se
       cade, i conti restano a schermo bloccati col motivo — non si impegna
       capitale su un saldo che non si è letto. */
    accounts.saldiTutti(segnale).catch(() => null),
  ]);

  return {
    ...vuoto,
    tipo: 'account',
    conti: elenco.data.filter((c) => c.id !== riferimento.accountId),
    saldiConti: saldi?.balances ?? null,
    saldiMuti: saldi == null,
    troncato: 0,
  };
}

/**
 * Dalle letture grezze ai bersagli, con i loro motivi di blocco.
 *
 * Sta qui, e non dentro un flusso, perché la clonazione e la creazione devono
 * vedere lo stesso elenco con gli stessi motivi: due letture diverse dello
 * stesso conto sono due verdetti diversi sulla stessa domanda — «questo conto
 * può finanziare il bot?» — e a rispondere male si impegna capitale su un conto
 * che non lo regge.
 */
export function costruisciBersagli(dati: DatiBersagli, riferimento: Tradebot): Bersaglio[] {
  if (dati.tipo === 'wallet') {
    return bersagliDaWallet(dati.portafogli, dati.riepiloghi, dati.dettagli, riferimento);
  }
  return bersagliDaConti(dati.conti, dati.saldiConti, dati.saldiMuti, riferimento);
}

/** Quanti bersagli ha davvero portato la lettura, prima di ogni giudizio. */
export function quantiBersagli(dati: DatiBersagli): number {
  return dati.tipo === 'wallet' ? dati.portafogli.length : dati.conti.length;
}
